// Helper functions to manage per-tab filter state in sessionStorage
// Make these globally available so facets.js can access them
window.getTabFilterState = function(tabIndex, collectionHandle) {
    try {
        const key = `collectionTab_${collectionHandle}_${tabIndex}`;
        const saved = sessionStorage.getItem(key);
        return saved ? JSON.parse(saved) : null;
    } catch (e) {
        return null;
    }
};

window.saveTabFilterState = function(tabIndex, collectionHandle, activePill, filterCheckboxes = null, sortBy = null) {
    try {
        const key = `collectionTab_${collectionHandle}_${tabIndex}`;
        const state = { activePill };
        
        // If filterCheckboxes provided, save them; otherwise keep existing
        if (filterCheckboxes !== null) {
            state.filterCheckboxes = filterCheckboxes;
        } else {
            // Preserve existing filter checkbox states
            const existing = window.getTabFilterState(tabIndex, collectionHandle);
            if (existing && existing.filterCheckboxes) {
                state.filterCheckboxes = existing.filterCheckboxes;
            }
        }
        
        // If sortBy provided, save it; otherwise try to get current value
        if (sortBy !== null) {
            state.sortBy = sortBy;
        } else {
            // Try to get the current sort value from DOM
            const collectionTabs = document.querySelector('.collection-tabs');
            if (collectionTabs) {
                const tabContent = collectionTabs.querySelector(`.tab-content[data-tab="${tabIndex}"]`);
                if (tabContent) {
                    const sortSelect = tabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
                    if (sortSelect && sortSelect.value) {
                        state.sortBy = sortSelect.value;
                    }
                }
            }
            
            // If still no sortBy, preserve existing
            if (!state.sortBy) {
                const existing = window.getTabFilterState(tabIndex, collectionHandle);
                if (existing && existing.sortBy) {
                    state.sortBy = existing.sortBy;
                }
            }
        }
        
        sessionStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
        // Silent failure
    }
};

window.clearTabFilterState = function(tabIndex, collectionHandle) {
    try {
        const key = `collectionTab_${collectionHandle}_${tabIndex}`;
        sessionStorage.removeItem(key);
    } catch (e) {
        // Silent failure
    }
};

// Keep local references for backward compatibility
const getTabFilterState = window.getTabFilterState;
const saveTabFilterState = window.saveTabFilterState;
const clearTabFilterState = window.clearTabFilterState;

// Get current filter checkbox states from the visible tab
function getCurrentFilterStates(tabContent) {
    const filterStates = {};
    const checkboxes = tabContent.querySelectorAll('input[type="checkbox"][name^="filter."]');
    
    checkboxes.forEach(checkbox => {
        const key = `${checkbox.name}::${checkbox.value}`;
        filterStates[key] = checkbox.checked;
    });
    
    return filterStates;
}

// Apply saved filter checkbox states to the visible tab
function restoreFilterStates(tabContent, filterStates) {
    if (!filterStates) {
        return;
    }
    
    // Set a flag to indicate we're restoring state (to prevent interference from other handlers)
    window.isRestoringCollectionTabFilters = true;
    
    const checkboxes = tabContent.querySelectorAll('input[type="checkbox"][name^="filter."]');
    
    checkboxes.forEach(checkbox => {
        const key = `${checkbox.name}::${checkbox.value}`;
        if (filterStates.hasOwnProperty(key)) {
            const shouldBeChecked = filterStates[key];
            if (checkbox.checked !== shouldBeChecked) {
                checkbox.checked = shouldBeChecked;
            }
        }
    });
    
    // Clear the flag after a short delay
    setTimeout(() => {
        window.isRestoringCollectionTabFilters = false;
    }, 100);
}

/* Initialize infinite scroll for a specific tab */
function initializeInfiniteScrollForTab(tabContent) {
    // Find the load more button within this specific tab
    const loadMoreBtn = tabContent.querySelector('[data-load-more]');
    
    if (!loadMoreBtn) {
        return; // No load more button in this tab
    }
    
    // Reset all load more buttons' initialization flags
    document.querySelectorAll('[data-load-more]').forEach(btn => {
        delete btn.dataset.infiniteScrollInit;
    });
    
    // Check if InfiniteScroll class exists
    if (typeof InfiniteScroll === 'undefined') {
        console.warn('InfiniteScroll class not found');
        return;
    }
    
    // Reset the singleton instance by setting it to null
    // This allows us to create a new instance for the current tab
    if (window.infiniteScrollInstance) {
        // Remove old event listener if it exists
        if (window.infiniteScrollInstance.documentClickHandler) {
            document.removeEventListener('click', window.infiniteScrollInstance.documentClickHandler, true);
        }
        window.infiniteScrollInstance = null;
    }
    
    try {
        // Create new instance with the tab content as the scope element
        // This ensures this instance always operates on this specific tab
        const instance = new InfiniteScroll(tabContent);
        // Store it globally so we can reset it later
        window.infiniteScrollInstance = instance;
    } catch (error) {
        console.error('Error initializing infinite scroll:', error);
    }
}

/* Initialize facets for a tab */
function initializeFacetsForTab(tabContent) {
    if (typeof FacetFiltersForm === 'undefined') {
        return;
    }
    
    const tabIndex = tabContent.dataset.tab;
    const collectionHandle = tabContent.dataset.collectionHandle;
    
    // Wire up refinement menus for the tab
    if (FacetFiltersForm.wireRefinementMenus) {
        FacetFiltersForm.wireRefinementMenus();
    }
    
    // Run cleanup functions now that this tab is visible
    if (FacetFiltersForm.removeBooleanOptions) {
        FacetFiltersForm.removeBooleanOptions();
    }
    
    if (FacetFiltersForm.dedupeFilterGroups) {
        FacetFiltersForm.dedupeFilterGroups();
    }
    
    // Re-initialize category pills for the newly visible tab
    const facetForm = tabContent.querySelector('facet-filters-form');
    if (facetForm && facetForm.initCategoryPills) {
        facetForm.initCategoryPills();
    }
    
    // Restore saved filter state for this tab
    const savedState = getTabFilterState(tabIndex, collectionHandle);
    if (savedState) {
        // Restore sort dropdown value
        if (savedState.sortBy) {
            const sortSelect = tabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
            if (sortSelect && sortSelect.value !== savedState.sortBy) {
                sortSelect.value = savedState.sortBy;
                // Adjust width if the function exists
                if (FacetFiltersForm && FacetFiltersForm.adjustSelectWidth) {
                    FacetFiltersForm.adjustSelectWidth(sortSelect);
                }
            }
        }
        
        // Restore filter checkbox states
        if (savedState.filterCheckboxes) {
            restoreFilterStates(tabContent, savedState.filterCheckboxes);
        }
        
        // Restore category pill state
        if (savedState.activePill) {
            // Find and click the saved pill to restore the filter
            const savedPill = tabContent.querySelector(`.category-pill[data-category-pill="${savedState.activePill}"]`);
            if (savedPill && !savedPill.classList.contains('active')) {
                // Simulate click to restore the filter
                setTimeout(() => {
                    savedPill.click();
                }, 100);
            }
        }
    } else {
        // No saved state - ensure "All" is selected
        // Update category pills active state for the newly visible tab
        if (FacetFiltersForm.updateCategoryPillsActiveState) {
            FacetFiltersForm.updateCategoryPillsActiveState();
        }
    }
    
    // Open filter sections that have active values
    if (FacetFiltersForm.openActiveFilters) {
        FacetFiltersForm.openActiveFilters();
    }
    
    // Reinitialize select widths now that the tab is visible
    // This fixes the issue where select dropdowns were too narrow in hidden tabs
    if (FacetFiltersForm.reinitializeSelectWidths) {
        FacetFiltersForm.reinitializeSelectWidths();
    }
    
    // Initialize infinite scroll for this tab
    if (typeof InfiniteScroll !== 'undefined') {
        initializeInfiniteScrollForTab(tabContent);
    }
}

/* Update Collection Tab in view based on dropdown selection. */
function update_collection_tab(item){
    const collectionTabs = item.closest('.collection-tabs');
    
    // Save current tab's active category pill, filter states, and sort value before switching
    const currentTabContent = collectionTabs.querySelector('.tab-content.visible');
    if (currentTabContent) {
        const currentTabIndex = currentTabContent.dataset.tab;
        const currentCollectionHandle = currentTabContent.dataset.collectionHandle;
        const activePill = currentTabContent.querySelector('.category-pill.active');
        const filterStates = getCurrentFilterStates(currentTabContent);
        
        // Get current sort value
        const sortSelect = currentTabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
        const sortBy = sortSelect ? sortSelect.value : null;
        
        if (activePill) {
            const activePillType = activePill.getAttribute('data-category-pill');
            saveTabFilterState(currentTabIndex, currentCollectionHandle, activePillType, filterStates, sortBy);
        }
    }
    
    // Remove active/visible from all tabs first
    const allTabButtons = collectionTabs.querySelectorAll('.tab');
    const allTabContents = collectionTabs.querySelectorAll('.tab-content');
    
    allTabButtons.forEach(btn => btn.classList.remove('active'));
    allTabContents.forEach(content => content.classList.remove('visible'));
    
    // Add active/visible to the selected tab
    const targetButton = collectionTabs.querySelector('.tab[data-tab="'+ item.dataset.tab +'"]');
    const targetContent = collectionTabs.querySelector('.tab-content[data-tab="'+ item.dataset.tab +'"]');
    
    if (targetButton) {
        targetButton.classList.add('active');
    }
    
    if (targetContent) {
        targetContent.classList.add('visible');
        
        // IMPORTANT: Use requestAnimationFrame to ensure the visible class is applied to DOM
        // before facets try to query for the visible tab
        requestAnimationFrame(() => {
            // Dispatch custom event
            const event = new CustomEvent('collectionTabChanged', {
                detail: {
                    tabIndex: item.dataset.tab,
                    collectionHandle: targetContent.dataset.collectionHandle,
                    collectionUrl: targetContent.dataset.collectionUrl
                }
            });
            document.dispatchEvent(event);
            
            // Initialize facets (DOM and visible class are now ready)
            initializeFacetsForTab(targetContent);
        });
    }
    
    // Note: We removed the filter clearing logic here because it was interfering
    // with the filter application system. The filtering system now properly
    // handles collection context switching through the facets.js AJAX system.
}

// Initialize tabs on page load using event-driven approach
function initializeCollectionTabs() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const tab = urlParams.get('tab');
    const requestedTab = document.querySelector('.collection-tabs .tab[data-variable="'+tab+'"');
    const firstTab = document.querySelector('.collection-tabs button.tab:first-child');

    if( tab && requestedTab ){
        requestedTab.click();
    } else if( firstTab ){
        firstTab.click();
    }
}

// Listen for category pill clicks and save the active state
document.addEventListener('click', function(e) {
    const categoryPill = e.target.closest('.category-pill');
    if (!categoryPill) return;
    
    // Check if we're in collection tabs
    const collectionTabs = categoryPill.closest('.collection-tabs');
    if (!collectionTabs) return;
    
    // Get the current tab info
    const tabContent = categoryPill.closest('.tab-content');
    if (!tabContent) return;
    
    const tabIndex = tabContent.dataset.tab;
    const collectionHandle = tabContent.dataset.collectionHandle;
    const pillType = categoryPill.getAttribute('data-category-pill');
    
    // Save the clicked pill state with current sort value
    setTimeout(() => {
        const filterStates = getCurrentFilterStates(tabContent);
        const sortSelect = tabContent.querySelector('select[name="sort_by"], select[id^="CollectionSortBy"]');
        const sortBy = sortSelect ? sortSelect.value : null;
        
        saveTabFilterState(tabIndex, collectionHandle, pillType, filterStates, sortBy);
    }, 100);
});

// Listen for filter checkbox changes and sort dropdown changes in collection tabs and save state
document.addEventListener('change', function(e) {
    // Check if we're in collection tabs first
    const collectionTabs = e.target.closest('.collection-tabs');
    if (!collectionTabs) {
        return;
    }
    
    // Get the current tab info
    const tabContent = e.target.closest('.tab-content.visible');
    if (!tabContent) {
        return;
    }
    
    const tabIndex = tabContent.dataset.tab;
    const collectionHandle = tabContent.dataset.collectionHandle;
    
    // Handle sort dropdown changes
    if (e.target.tagName === 'SELECT' && (e.target.name === 'sort_by' || e.target.id && e.target.id.startsWith('CollectionSortBy'))) {
        const sortBy = e.target.value;
        
        // Get current states
        const activePill = tabContent.querySelector('.category-pill.active');
        const activePillType = activePill ? activePill.getAttribute('data-category-pill') : 'all';
        const filterStates = getCurrentFilterStates(tabContent);
        
        console.log('[SORT CHANGE] Sort dropdown changed:', {
            sortBy,
            activePillType,
            tabIndex,
            collectionHandle,
            filterStates
        });
        
        // Save with the new sort value
        saveTabFilterState(tabIndex, collectionHandle, activePillType, filterStates, sortBy);
        return;
    }
    
    // Handle filter checkboxes
    if (e.target.type === 'checkbox' && e.target.name && e.target.name.startsWith('filter.')) {
        // Skip if we're currently restoring state
        if (window.isRestoringCollectionTabFilters) {
            return;
        }
        
        // Skip if we're clearing all filters (to prevent saving the cleared state)
        if (window.skipCollectionTabRestoration) {
            return;
        }
        
        // Capture the clicked checkbox details NOW (before any async operations)
        const clickedCheckbox = {
            name: e.target.name,
            value: e.target.value,
            checked: e.target.checked
        };
        
        // Use setTimeout to ensure state is captured after checkbox updates
        setTimeout(() => {
            const filterStates = getCurrentFilterStates(tabContent);
            
            // Ensure the clicked checkbox state is correctly reflected
            const key = `${clickedCheckbox.name}::${clickedCheckbox.value}`;
            filterStates[key] = clickedCheckbox.checked;
            
            const activePill = tabContent.querySelector('.category-pill.active');
            const activePillType = activePill ? activePill.getAttribute('data-category-pill') : 'all';
            
            // Save state - sortBy will be auto-captured from DOM in saveTabFilterState
            saveTabFilterState(tabIndex, collectionHandle, activePillType, filterStates);
        }, 10); // Slightly longer delay to ensure DOM updates
    }
}, true); // Use capture phase to ensure we save before other handlers

// Wait for both DOM and facets.js to be ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCollectionTabs);
} else {
    initializeCollectionTabs();
}

// Clear all collection tab filter states when navigating away from the page
window.addEventListener('beforeunload', function() {
    // Only clear if we're on a collection tabs page
    const collectionTabs = document.querySelector('.collection-tabs');
    if (collectionTabs) {
        try {
            // Get all tab contents to find their collection handles
            const allTabContents = collectionTabs.querySelectorAll('.tab-content');
            allTabContents.forEach(tabContent => {
                const tabIndex = tabContent.dataset.tab;
                const collectionHandle = tabContent.dataset.collectionHandle;
                if (tabIndex && collectionHandle) {
                    window.clearTabFilterState(tabIndex, collectionHandle);
                }
            });
        } catch (e) {
            // Silent failure
        }
    }
});

// Also clear session storage when clicking on navigation links that leave the collection tabs page
document.addEventListener('click', function(e) {
    const link = e.target.closest('a[href]');
    if (!link) return;
    
    // Check if we're on a collection tabs page
    const collectionTabs = document.querySelector('.collection-tabs');
    if (!collectionTabs) return;
    
    const href = link.getAttribute('href');
    
    // Skip if it's just a hash link or empty
    if (!href || href.startsWith('#') || href === '') return;
    
    // Skip if it's the current collection tabs page (tab switching)
    if (link.closest('.collection-tabs')) {
        // Allow tab switching within the same collection tabs component
        if (link.classList.contains('tab')) return;
    }
    
    // Clear all tab filter states when navigating to a different page
    try {
        const allTabContents = collectionTabs.querySelectorAll('.tab-content');
        allTabContents.forEach(tabContent => {
            const tabIndex = tabContent.dataset.tab;
            const collectionHandle = tabContent.dataset.collectionHandle;
            if (tabIndex && collectionHandle) {
                window.clearTabFilterState(tabIndex, collectionHandle);
            }
        });
    } catch (e) {
        // Silent failure
    }
});

/*
document.addEventListener("click", function(e){
    const collectionTab = (e.target.classList.contains('tab') ? e.target : false);
    if (e.target == collectionTab) {
        document.getElementById("SkipToMainContent").scrollIntoView({ behavior: "smooth", block: "start" });
    }

});
*/